import React, { useState } from "react";
import Swal from "sweetalert2";
import { motion } from "framer-motion";

export default function FileDropzone({ setOpenDragDrop, setBase64Strings, setHasAttachment }) {
  const [selectedFiles, setSelectedFiles] = useState([]);

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(",")[1];
        resolve({
          name: file.name,
          contentType: file.type,
          size: file.size,
          base64: base64String,
        });
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (selectedFiles) => {
    setOpenDragDrop(false);
    const fileDataPromises = selectedFiles.map((file) => convertFileToBase64(file));

    try {
      const base64Results = await Promise.all(fileDataPromises);
      console.log(base64Results);
      setBase64Strings((prevBase64Strings) => [...prevBase64Strings, ...base64Results]);
      setHasAttachment(true);
    } catch (error) {
      console.error("Error converting files: ", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to convert the attachments into base64!",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
    >
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b bg-[#365561] text-white">
          <p className="text-sm font-semibold">Upload Files</p>
          <i
            className="fa-solid fa-xmark text-white cursor-pointer hover:text-gray-300"
            onClick={() => setOpenDragDrop(false)}
          />
        </div>

        {/* Dropzone */}
        <div className="p-4 space-y-4 flex-1 overflow-y-auto">
          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50 cursor-pointer hover:border-blue-400 transition"
          >
            <p className="text-sm text-gray-600">Drag & Drop files here</p>
            <label
              htmlFor="fileInput"
              className="text-blue-600 font-medium cursor-pointer hover:underline"
            >
              Browse Files
            </label>
            <input
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              id="fileInput"
            />
          </div>

          {selectedFiles.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-800 mb-1">Selected Files:</p>
              <ul className="space-y-2 max-h-40 overflow-y-auto pr-1">
                {selectedFiles.map((file, index) => {
                  const fileType = file.type;
                  let icon = "fa-file";

                  if (fileType.includes("image")) icon = "fa-file-image";
                  else if (fileType.includes("pdf")) icon = "fa-file-pdf";
                  else if (fileType.includes("word")) icon = "fa-file-word";
                  else if (fileType.includes("excel") || fileType.includes("spreadsheet")) icon = "fa-file-excel";
                  else if (fileType.includes("powerpoint")) icon = "fa-file-powerpoint";
                  else if (fileType.includes("zip") || fileType.includes("compressed")) icon = "fa-file-zipper";
                  else if (fileType.includes("audio")) icon = "fa-file-audio";
                  else if (fileType.includes("video")) icon = "fa-file-video";

                  return (
                    <li key={index} className="flex items-center justify-between bg-gray-100 px-3 py-2 rounded-md shadow-sm">
                      <div className="flex items-center gap-3 overflow-hidden">
                        <i className={`fa-solid ${icon} text-gray-600 text-lg`} />
                        <span className="text-sm text-gray-800 truncate">{file.name}</span>
                      </div>
                      <button
                        onClick={() => {
                          const updated = [...selectedFiles];
                          updated.splice(index, 1);
                          setSelectedFiles(updated);
                        }}
                        className="text-red-500 hover:text-red-700 text-sm"
                        title="Remove"
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        {/* Footer Button */}
        <div className="px-4 py-3 border-t flex justify-center bg-gray-50">
          <button
            className="flex items-center gap-2 text-white bg-[#365561] hover:bg-[#2f4751] transition px-4 py-2 rounded-md text-sm"
            onClick={() => handleFileChange(selectedFiles)}
          >
            <i className="fa-regular fa-circle-check text-base"></i>
            <span>Attach Files</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
}